import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/format';
import { processingStatusLabel } from '@/components/status-badge';
import { cn } from '@/lib/utils';
import type { CatalogueFilters, FilterChip, FilterChipKind } from '@/lib/filters';

interface Lookups {
  categoryLabels: Map<string, string>;
  sharerLabels: Map<string, string>;
  channelLabels: Map<string, string>;
}

/** Derives one chip per active filter, in a stable, readable order. */
export function buildFilterChips(filters: CatalogueFilters, lookups: Lookups): FilterChip[] {
  const chips: FilterChip[] = [];

  if (filters.query.trim()) {
    chips.push({ id: 'query', kind: 'query', label: 'Search', value: filters.query.trim() });
  }
  for (const slug of filters.categories) {
    chips.push({
      id: `category:${slug}`,
      kind: 'category',
      label: 'Category',
      value: lookups.categoryLabels.get(slug) ?? slug,
    });
  }
  if (filters.sharerId) {
    chips.push({
      id: 'sharer',
      kind: 'sharer',
      label: 'First shared by',
      value: lookups.sharerLabels.get(filters.sharerId) ?? filters.sharerId,
    });
  }
  if (filters.channelId) {
    chips.push({
      id: 'channel',
      kind: 'channel',
      label: 'First seen in',
      value: lookups.channelLabels.get(filters.channelId) ?? filters.channelId,
    });
  }
  if (filters.domain) {
    chips.push({ id: 'domain', kind: 'domain', label: 'Domain', value: filters.domain });
  }
  if (filters.from) {
    chips.push({ id: 'from', kind: 'from', label: 'From', value: formatDate(filters.from) });
  }
  if (filters.to) {
    chips.push({ id: 'to', kind: 'to', label: 'To', value: formatDate(filters.to) });
  }
  if (filters.status) {
    chips.push({
      id: 'status',
      kind: 'status',
      label: 'Status',
      value: processingStatusLabel(filters.status),
    });
  }
  if (filters.archived) {
    chips.push({ id: 'archived', kind: 'archived', label: 'Showing', value: 'Archived links' });
  }

  return chips;
}

export function FilterChips({
  chips,
  onRemove,
  onClearAll,
  className,
}: {
  chips: FilterChip[];
  onRemove: (kind: FilterChipKind, value?: string) => void;
  onClearAll: () => void;
  className?: string;
}) {
  if (chips.length === 0) return null;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span className="text-xs font-medium text-muted-foreground">Active filters:</span>
      <ul className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => (
          <li key={chip.id}>
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary py-0.5 pl-2.5 pr-1 text-xs text-secondary-foreground">
              <span className="text-muted-foreground">{chip.label}:</span>
              <span className="max-w-[16rem] truncate font-medium">{chip.value}</span>
              <button
                type="button"
                onClick={() =>
                  onRemove(
                    chip.kind,
                    chip.kind === 'category' ? chip.id.slice('category:'.length) : undefined,
                  )
                }
                className={cn(
                  'ml-0.5 rounded-full p-0.5 text-muted-foreground transition-colors',
                  'hover:bg-background hover:text-foreground focus-visible:outline-none',
                  'focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <X aria-hidden="true" className="h-3 w-3" />
                <span className="sr-only">
                  Remove filter {chip.label}: {chip.value}
                </span>
              </button>
            </span>
          </li>
        ))}
      </ul>
      <Button type="button" variant="ghost" size="sm" onClick={onClearAll}>
        Clear all
      </Button>
    </div>
  );
}
